import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ANNOTATIONS_ENABLED } from 'shared/config/featureFlags';
import { useTeam } from 'shared/context/teamContext';
import * as api from './annotationApi';
import { resolveStoredUser } from './annotationSelectors';

const AnnotationContext = createContext(null);

function teamKeyFromSelected(selectedTeam) {
  if (!selectedTeam) return '';
  if (typeof selectedTeam === 'string') return selectedTeam;
  return selectedTeam.id || selectedTeam.name || '';
}

function upsertThread(list, thread) {
  if (!thread) return list;
  const without = list.filter((a) => String(a.id) !== String(thread.id));
  return [thread, ...without];
}

/**
 * A poll that found nothing new must not produce a new array.
 *
 * AnnotationList refetches every 5s so Slack replies appear without a reload.
 * Each refetch used to call setAnnotations with a freshly built array, which
 * changed the context value, which re-rendered every consumer — the runs
 * table and its badges included — five times a minute whether or not a single
 * comment had changed. That is the lag: not the queries, which are now within
 * noise of a bare SELECT 1, but the render.
 *
 * Identity here is what a poll can actually change: the comment's text, its
 * edited flag, and the set of replies with theirs.
 */
function threadStamp(a) {
  const replies = (a.replies || []).map((r) => `${r.id}|${r.body || ''}|${r.edited ? 'e' : ''}`).join(',');
  return `${a.id}|${a.body || ''}|${a.edited ? 'e' : ''}|[${replies}]`;
}

function sameThreads(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (threadStamp(a[i]) !== threadStamp(b[i])) return false;
  }
  return true;
}

/** setAnnotations that keeps the old array when nothing actually moved. */
function stableSet(setter, build) {
  setter((prev) => {
    const next = build(prev);
    return sameThreads(prev, next) ? prev : next;
  });
}

export function AnnotationProvider({ children }) {
  const { selectedTeam } = useTeam();
  const teamId = teamKeyFromSelected(selectedTeam);
  const [annotations, setAnnotations] = useState([]);
  const [mentionUsers, setMentionUsers] = useState([]);
  const [counts, setCounts] = useState({ conversations: {}, runs: {}, spans: {} });

  useEffect(() => {
    setAnnotations([]);
    setCounts({ conversations: {}, runs: {}, spans: {} });
  }, [teamId]);

  useEffect(() => {
    if (!ANNOTATIONS_ENABLED || !teamId) {
      setMentionUsers([]);
      return undefined;
    }
    let cancelled = false;
    api.fetchMentionUsers(teamId).then((users) => {
      if (!cancelled) setMentionUsers(users);
    }).catch(() => {
      if (!cancelled) setMentionUsers([]);
    });
    return () => { cancelled = true; };
  }, [teamId]);

  const currentUser = useMemo(() => {
    const stored = resolveStoredUser();
    const match = mentionUsers.find((u) => {
      if (stored.id != null && Number(u.id) === Number(stored.id)) return true;
      if (stored.email && String(u.email || '').trim().toLowerCase() === stored.email) return true;
      return false;
    });
    if (match) return match;
    if (stored.id == null && !stored.email) return { id: null, email: null, name: 'You' };
    return {
      id: stored.id,
      email: stored.email,
      name: stored.name || 'You',
    };
  }, [mentionUsers]);

  const replaceThread = useCallback((thread) => {
    setAnnotations((prev) => upsertThread(prev, thread));
  }, []);

  const loadConversation = useCallback(async (conversationId) => {
    if (!ANNOTATIONS_ENABLED || !teamId || !conversationId) return;
    const list = await api.listAnnotations(teamId, conversationId);
    stableSet(setAnnotations, (prev) => {
      const others = prev.filter((a) => String(a.conversationId) !== String(conversationId));
      return [...list, ...others];
    });
  }, [teamId]);

  const loadRun = useCallback(async (runId, conversationId) => {
    if (!ANNOTATIONS_ENABLED || !teamId || !runId) return;
    let list;
    try {
      list = await api.listAnnotationsForRun(teamId, runId, conversationId);
    } catch {
      if (!conversationId) return;
      list = await api.listAnnotations(teamId, conversationId);
    }
    stableSet(setAnnotations, (prev) => {
      const keep = prev.filter((a) => {
        const isThisRun = a.context?.type === 'run' && String(a.context.id) === String(runId);
        const isThisRunSpan = a.context?.type === 'span' && String(a.context.runId || '') === String(runId);
        return !isThisRun && !isThisRunSpan;
      });
      return [...list, ...keep];
    });
  }, [teamId]);

  const prefetchCounts = useCallback(async ({ conversationIds = [], runIds = [], spans = [] }) => {
    if (!ANNOTATIONS_ENABLED || !teamId) return;
    if (!conversationIds.length && !runIds.length && !spans.length) return;
    try {
      const next = await api.fetchAnnotationCounts(teamId, { conversationIds, runIds, spans });
      setCounts((prev) => ({
        conversations: { ...prev.conversations, ...(next.conversations || {}) },
        runs: { ...prev.runs, ...(next.runs || {}) },
        spans: { ...prev.spans, ...(next.spans || {}) },
      }));
    } catch {
      // badges stay hidden
    }
  }, [teamId]);

  /**
   * The comment appears the moment you send it.
   *
   * Posting a comment is eight sequential round trips on the server (team,
   * conversation, context validation, mention check, then BEGIN/INSERT/
   * UPDATE/COMMIT) before the row comes back. Waiting for all of that with an
   * empty box on screen is what made writing a comment feel slow. So the
   * comment is rendered from what the author typed, with a pending flag, and
   * replaced by the server's row when it lands. If the post fails the
   * placeholder is removed and the error is re-thrown for the caller to show.
   */
  const optimisticId = useRef(0);

  const createAnnotation = useCallback(async (payload) => {
    optimisticId.current += 1;
    const tempId = `pending-${optimisticId.current}`;
    const placeholder = {
      id: tempId,
      pending: true,
      conversationId: payload.conversationId,
      context: payload.context || null,
      body: payload.body,
      mentions: payload.mentions || [],
      author: { id: currentUser?.id ?? null, name: currentUser?.name || 'You' },
      createdAt: new Date().toISOString(),
      replies: [],
    };
    setAnnotations((prev) => [placeholder, ...prev]);
    try {
      const created = await api.createAnnotation(teamId, payload);
      setAnnotations((prev) => upsertThread(prev.filter((a) => a.id !== tempId), created));
      return created;
    } catch (err) {
      setAnnotations((prev) => prev.filter((a) => a.id !== tempId));
      throw err;
    }
  }, [teamId, currentUser]);

  const replyToAnnotation = useCallback(async (annotationId, payload) => {
    optimisticId.current += 1;
    const tempId = `pending-${optimisticId.current}`;
    const placeholder = {
      id: tempId,
      pending: true,
      body: payload.body,
      mentions: payload.mentions || [],
      author: { id: currentUser?.id ?? null, name: currentUser?.name || 'You' },
      createdAt: new Date().toISOString(),
    };
    setAnnotations((prev) => prev.map((a) => (String(a.id) === String(annotationId)
      ? { ...a, replies: [...(a.replies || []), placeholder] }
      : a)));
    try {
      const thread = await api.replyToAnnotation(teamId, annotationId, payload);
      replaceThread(thread);
      return thread;
    } catch (err) {
      setAnnotations((prev) => prev.map((a) => (String(a.id) === String(annotationId)
        ? { ...a, replies: (a.replies || []).filter((r) => r.id !== tempId) }
        : a)));
      throw err;
    }
  }, [teamId, replaceThread, currentUser]);

  /**
   * Edit an existing comment. The server has always accepted PATCH
   * /annotations/:id — author or team admin only — but nothing on the client
   * ever called it, so the row menu offered Delete as the only way to fix a
   * typo.
   */
  const updateAnnotation = useCallback(async (annotationId, payload) => {
    const before = { body: null, mentions: null };
    setAnnotations((prev) => prev.map((a) => {
      if (String(a.id) === String(annotationId)) {
        before.body = a.body; before.mentions = a.mentions;
        return { ...a, body: payload.body, mentions: payload.mentions || [], edited: true };
      }
      return {
        ...a,
        replies: (a.replies || []).map((r) => {
          if (String(r.id) !== String(annotationId)) return r;
          before.body = r.body; before.mentions = r.mentions;
          return { ...r, body: payload.body, mentions: payload.mentions || [], edited: true };
        }),
      };
    }));
    try {
      const thread = await api.updateAnnotation(teamId, annotationId, payload);
      if (thread) replaceThread(thread);
      return thread;
    } catch (err) {
      if (before.body != null) {
        setAnnotations((prev) => prev.map((a) => (String(a.id) === String(annotationId)
          ? { ...a, body: before.body, mentions: before.mentions || [] }
          : {
            ...a,
            replies: (a.replies || []).map((r) => (String(r.id) === String(annotationId)
              ? { ...r, body: before.body, mentions: before.mentions || [] }
              : r)),
          })));
      }
      throw err;
    }
  }, [teamId, replaceThread]);

  const deleteAnnotation = useCallback(async (annotationId) => {
    await api.deleteAnnotation(teamId, annotationId);
    setAnnotations((prev) => prev
      .filter((a) => String(a.id) !== String(annotationId))
      .map((a) => (a.replies?.length
        ? { ...a, replies: a.replies.filter((r) => String(r.id) !== String(annotationId)) }
        : a)));
  }, [teamId]);

  const addMention = useCallback((mentions, user) => api.addMention(mentions, user), []);

  const value = useMemo(() => ({
    enabled: ANNOTATIONS_ENABLED,
    teamId,
    annotations,
    counts,
    currentUser,
    mentionUsers,
    loadConversation,
    loadRun,
    prefetchCounts,
    createAnnotation,
    replyToAnnotation,
    updateAnnotation,
    deleteAnnotation,
    addMention,
  }), [
    annotations,
    counts,
    currentUser,
    mentionUsers,
    teamId,
    loadConversation,
    loadRun,
    prefetchCounts,
    createAnnotation,
    replyToAnnotation,
    updateAnnotation,
    deleteAnnotation,
    addMention,
  ]);

  return (
    <AnnotationContext.Provider value={value}>
      {children}
    </AnnotationContext.Provider>
  );
}

export function useAnnotations() {
  const ctx = useContext(AnnotationContext);
  if (!ctx) {
    throw new Error('useAnnotations must be used within AnnotationProvider');
  }
  return ctx;
}
